import {
  ChevronLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import Tabs from "components/lib/Shared/Tabs";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _merge from "lodash/merge";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import { useToast } from "hooks/useToast";
import { GENERATE_TABS } from "utils/generateChangesTabs";
import SectionDescription from "components/lib/Shared/SectionDescription";
import NamesForm from "components/lib/Shared/Forms/Names";
import IdentificationDetailsForm from "../../Changes/OfficialsShareholdersAuthorisedPersons/ChangeOfParticulars/ChangeOfParticularsSubRow/IdentificationDetails";
import ManageAppointmentDate from "../../Changes/OfficialsShareholdersAuthorisedPersons/ChangeOfParticulars/ChangeOfParticularsSubRow/EffectiveDates/AppointmentDate";
import ResidentialDetails from "./ResidentialDetails";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getDocumentData } from "config/changesDocumentData";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { Constants } from "config/constants";
import { transactionReviewAsync } from "state/slices/initiateTransactionChange";
import ChangedDocumentsUpdated from "components/lib/Shared/ChangedDocuments/docsUpdatedUpload";

const ApplicationCorrections = ({
  onComplete,
  row,
  visibleColumns,
  setShowCorrectionModal,
  touched = false,
}) => {
  const dispatch = useDispatch();
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const currentUser = useSelector((state) => state.userSlice);

  const { comments } = useSelector((state) => state.commentsSlice);
  const documentData = getDocumentData(row);

  const generalInformationCommentsCount = useMemo(() => {
    return (
      comments.filter(
        (comment) =>
          comment.formName === "identificationDetails" ||
          comment.formName === "names"
      )?.length ?? 0
    );
  }, [comments]);

  const residentialDetailsCommentsCount = useMemo(() => {
    return (
      comments.filter(
        (comment) =>
          comment.formName === "postalAddress" ||
          comment.formName === "electronicAddress" ||
          comment.formName === "residentialAddressDetails" ||
          comment.formName === "isNonResident"
      )?.length ?? 0
    );
  }, [comments]);

  const fileUploadCommentsCount = useMemo(() => {
    return (
      comments.filter((comment) => comment.formName === "uploadDocuments")
        ?.length ?? 0
    );
  }, [comments]);

  const changeDocumentsCommentsCount = useMemo(() => {
    return (
      comments.filter((comment) => comment.formName === "uploadChangeDocuments")
        ?.length ?? 0
    );
  }, [comments]);

  const { correctionTabs } = useMemo(() => {
    const {
      INITIAL_TABS,
      SHORT_FORM_TABS,
      LONG_FORM_TABS,
      APPLICATION_CORRECTION_TABS,
    } = GENERATE_TABS({
      generalInformationCommentsCount,
      residentialDetailsCommentsCount,
      fileUploadCommentsCount,
      changeDocumentsCommentsCount,
    });
    const initialTabs = INITIAL_TABS;

    const shortFormTabs = SHORT_FORM_TABS;

    const longFormTabs = LONG_FORM_TABS;

    const correctionTabs = APPLICATION_CORRECTION_TABS;

    return { initialTabs, shortFormTabs, longFormTabs, correctionTabs };
  }, [
    generalInformationCommentsCount,
    residentialDetailsCommentsCount,
    fileUploadCommentsCount,
    changeDocumentsCommentsCount,
  ]);

  const [tabs, setTabs] = useState(correctionTabs);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState({});

  const goToPrevStep = () => {
    if (selectedTabIndex === 0) handleBackBtn();
    else setSelectedTabIndex(selectedTabIndex - 1);
  };

  const goToNextStep = () => {
    setSelectedTabIndex(selectedTabIndex + 1);
  };

  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  const [initialValues, setInitialValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const formStepErrors = useRef(null);
  const { toast } = useToast(4000);

  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = correctionTabs.length - 1 === selectedTabIndex;

  const submitHandler = async () => {
    setSubmitting(true);
    const currentStatus = row.status;
    let toastMessage = "";
    let status = "";
    try {
      switch (currentStatus) {
        case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_CORRECTIONS:
          ({ status, toastMessage } = await handleDirectorReview());
          break;
        case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_CORRECTIONS:
          ({ status, toastMessage } = await handleShareholderReview());
          break;
        case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CORRECTION:
          ({ status, toastMessage } = await handleRegistrarReview());
          break;
        case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_CORRECTIONS:
          ({ status, toastMessage } = await handleAdminReview());
          break;
        case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_CORRECTION:
          ({ status, toastMessage } = await handleBRSReview());
          break;
        default:
          console.warn("Unknown status");
          break;
      }

      await dispatch(
        transactionReviewAsync({
          companyId: row.companyId,
          data: {
            trxRef: row.trxRef,
            officialId: row.officialId,
            status,
            infoForEmailToBeSent: {
              name: row.names.firstName,
              email: row.electronicAddress.emailAddress,
              reviewer: currentUser.names.firstName,
              companyName: row.company.companyName1WithSuffix,
              unresolvedCommentsCount: 0,
            },
          },
        })
      );
      toast("success", toastMessage);
      // !showOrgAdminSuccessModal &&
      //   status !== "ADMIN-REVIEW-II" &&
      onComplete("Correction is done successfully");

      setSubmitting(false);
      setShowCorrectionModal(false);
    } catch (error) {
      console.error("Error in handleSubmitApplication:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegistrarReview = () => {
    let status = "";
    let toastMessage = "";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II;
    toastMessage = "The Application sent to Registrar for review II";
    return { status, toastMessage };
  };

  const handleDirectorReview = async () => {
    let status = "";
    let toastMessage = "";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_REVIEW_II;
    toastMessage = "The Application sent to Director for review II";
    return { status, toastMessage };
  };

  const handleShareholderReview = async () => {
    let status = "";
    let toastMessage = "";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_REVIEW_II;
    toastMessage = "The Application sent to Shareholder for review II";
    return { status, toastMessage };
  };

  const handleAdminReview = () => {
    let status = "";
    let toastMessage = "";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II;
    toastMessage = "The Application sent to Admin for review II";
    return { status, toastMessage };
  };

  const handleBRSReview = () => {
    let status = "";
    let toastMessage = "";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II;
    toastMessage = "The Application sent to BRS for review II";
    return { status, toastMessage };
  };

  return (
    <>
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className={`px-9`}>
          <div className="bg-white mt-12 mb-2 rounded-md shadow-lg py-4 px-4">
            <Formik
              initialValues={{
                role: [],
                ...initialValues,
              }}
            >
              {({ errors, values }) => {
                useEffect(() => {
                  setInitialValues(_merge(values, row));
                }, [row]);
                return (
                  <Form>
                    {formStepErrors.current?.[selectedTab.id]?.length > 0 && (
                      <ErrorBanner
                        errors={[
                          ...(formStepErrors.current?.[selectedTab.id] ?? []),
                        ]}
                        className="my-4"
                      />
                    )}
                    <div className="relative p-6">
                      <div className="flex justify-between">
                        <div className="flex flex-col mt-2">
                          <h3 className="font-extrabold text-2xl mt-4 text-slate-800">
                            Application Correction
                          </h3>
                        </div>
                        <div className="flex flex-col justify-center px-4 py-2 text-sm font-medium border border-transparent rounded-md shadow-sm bg-[#F2F2F2] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                          <div>
                            <p className="w-full mb-1 flex justify-between items-center">
                              <span className="text-sm font-medium text-primary">
                                Reviewer is:
                              </span>
                              <b className="text-sm text-white bg-primary px-4 py-1 rounded-md">
                                {values?.status?.split("-")[0].toLowerCase()}
                              </b>
                            </p>
                          </div>
                          <div className="flex items-baseline">
                            <label className="text-base font-medium text-gray-800">
                              Current Status:
                            </label>
                          </div>
                          <hr className="w-full border-gray-300 my-1" />
                          <label className="text-sm space-x-2">
                            {getMaintainYourCompanyReviewStatus(values?.status)}
                          </label>
                        </div>
                      </div>

                      <div className=" mt-4 mb-4">
                        <div className="rounded-md bg-sky-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <InformationCircleIcon
                                className="h-5 w-5 text-sky-400"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="ml-3 flex-1 md:flex md:justify-between whitespace-normal">
                              <p className="text-sm text-sky-700">
                                Please made correction as feedback. please
                                Submit these corrections once done to Reviewer
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <Tabs
                        tabs={correctionTabs}
                        selectedTabIndex={selectedTabIndex}
                        setSelectedTabIndex={setSelectedTabIndex}
                      />
                      {selectedTab?.id === "generalInformation" && (
                        <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                          <div className="px-4 py-6 space-y-6 sm:p-6">
                            <div className="mb-4">
                              <h3 className="text-lg font-medium leading-6 text-gray-900">
                                Personal Information
                              </h3>
                              <SectionDescription description="To activate the form fields please verify your ID first" />
                            </div>
                            <>
                              {[
                                "Kenyan Citizen",
                                "Foreign Resident",
                                "Foreigner",
                                "Minor",
                                "Local Company",
                                "Foreign Company",
                                "Other e.g Parastatals",
                              ].includes(row.official.idType) && (
                                <>
                                  <Field
                                    name="names"
                                    component={NamesForm}
                                    idType={row.official.idType}
                                    // disabled={disableForm}
                                    disabledFields={[
                                      "firstName",
                                      "lastName",
                                      "fullName",
                                    ]}
                                    disableVerificationField={true}
                                  />
                                </>
                              )}
                              <hr />
                              <IdentificationDetailsForm
                                idType={row.official.idType}
                                designation={row.official.designation}
                                disabled={
                                  generalInformationCommentsCount > 0
                                    ? false
                                    : true
                                }
                                disableVerificationField={true}
                                disableEmailAddress={
                                  true && values?.type === "invited"
                                }
                                disableContactName={
                                  true && values?.type === "invited"
                                }
                                disableTelNumber={
                                  true && values?.type === "invited"
                                }
                              />
                              <hr />
                              <div className="grid grid-cols-6 gap-6">
                                <Field
                                  name="official"
                                  component={ManageAppointmentDate}
                                  idType={row.official.idType}
                                  designation={row.official.designation}
                                  // disabled={disableForm}
                                />
                              </div>
                            </>
                          </div>
                        </div>
                      )}
                      {/*  Address Particulars */}
                      {selectedTab.id === "residentialDetails" && (
                        <ResidentialDetails
                          designation={row.official.designation}
                          idType={row.official.idType}
                          disabled={
                            residentialDetailsCommentsCount > 0 ? false : true
                          }
                          errors={errors}
                          touched={touched}
                          isNonResident={row.official.isNonResident}
                        />
                      )}
                      {/* File Uploads Form */}
                      {selectedTab.id === "uploadDocuments" && (
                        <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                          <div className="px-4 py-6 sm:p-6">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              KYC Document Uploads
                            </h3>
                            {[
                              "Kenyan Citizen",
                              "Foreign Resident",
                              "Foreigner",
                              "Minor",
                              "Local Company",
                              "Foreign Company",
                              "Other e.g Parastatals",
                            ].includes(row.official.idType) && (
                              <div className="mb-2">
                                <p>
                                  Please upload the correct documents as
                                  instructed
                                </p>
                              </div>
                            )}
                            <Field
                              name="official.fileUploads"
                              component={FileUploadForm}
                              documentData={documentData}
                              disabled={
                                fileUploadCommentsCount > 0 ? false : true
                              }
                            />
                          </div>
                        </div>
                      )}
                      {selectedTab.id === "uploadChangeDocuments" && (
                        <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md py-4 px-2">
                          <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md py-4 px-2">
                            <Field
                              name="uploadChangeDocuments"
                              component={ChangedDocumentsUpdated}
                              disable={
                                changeDocumentsCommentsCount > 0 ? false : true
                              }
                              idType={row.idType}
                              designation={row.official.designation}
                              uploadRequestType={"appointing-official"}
                              documentsFor={"uploadChangeDocuments"}
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between">
                        {!isFirstStep && (
                          <button
                            onClick={goToPrevStep}
                            className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                            type="button"
                          >
                            <span className="flex items-center mr-4 h-7">
                              <ChevronLeftIcon
                                className="flex-shrink-0 w-5 h-5"
                                aria-hidden="true"
                              />
                            </span>
                            <p className="font-semibold">Go back</p>
                          </button>
                        )}
                        {isLastStep ? (
                          <Button
                            type="button"
                            disabled={submitting}
                            onClick={submitHandler}
                            className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7 disabled:cursor-not-allowed"
                          >
                            {submitting && isLastStep ? (
                              <>
                                <SpinnerIcon />
                                Submitting...
                              </>
                            ) : (
                              "Submit for Review"
                            )}
                          </Button>
                        ) : (
                          <Button
                            className=" flex items-start float-right py-2 ml-auto font-semibold rounded mt-7 mb-4"
                            onClick={goToNextStep}
                          >
                            Save and Proceed
                          </Button>
                        )}
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </td>
      </tr>
    </>
  );
};

export default ApplicationCorrections;
